<template>
  <Dropdown v-model="show" class="mr-3" class-prop="lg:w-112" mobile-title="Price">
    <template #button>
      <button
        class="whitespace-nowrap rounded bg-gray-light p-3 font-medium hover:text-blue focus:outline-none"
        :class="{ 'text-blue': show || filters.min_price || filters.max_price }"
        @click="show = !show"
      >
        <span v-if="filters.min_price || filters.max_price">
          {{ $f.currency(filters.min_price, currency) }}
          -
          {{ $f.currency(filters.max_price, currency) }}
        </span>
        <span v-else>Price</span>
      </button>
    </template>
    <div>
      <div class="px-2">
        <div class="mb-4 text-right">
          <DropdownSelect :value="currency.code" :options="selectCurrencies" mobile-title="Sort by" @input="code => updateCurrency(code)">
            <template #button="{ click }">
              <button class="focused:outline-none text-sm text-body" @click="click">
                <span class="inline-block">{{ selectCurrencies.length && selectCurrencies.find(c => c.id === currency.code).label }}</span>
                <svg
                  class="inline-block"
                  width="8"
                  height="5"
                  viewBox="0 0 8 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M0 0.649682L0.649682 0L4 3.35032L7.35032 0L8 0.649682L4 4.64968L0 0.649682Z" fill="#808080" />
                </svg>
              </button>
            </template>
          </DropdownSelect>
        </div>

        <PricesChart :prices="prices" :min-price="minPrice" :max-price="maxPrice" :show="show" />
        <VueSlider
          v-model="sliderValue"
          :min="minPrice"
          :max="maxPrice"
          tooltip="none"
          :tooltip-formatter="v => `$${v}`"
          :enable-cross="false"
          class="-mt-3"
          :min-range="10"
          :dot-size="24"
        />
      </div>
      <div class="mb-4 mt-10 flex justify-between">
        <div class="grow">
          <label for="min_price" class="text-sm text-gray-3">min price</label>
          <div class="flex w-full rounded bg-gray-6 px-4 text-lg font-medium leading-10">
            <span>{{ symbol }}</span>
            <input
              id="min_price"
              :value="Math.round(sliderValue[0] * rate)"
              type="number"
              :min="Math.round(minPrice * rate)"
              :max="Math.round(maxPrice * rate)"
              class="w-full bg-gray-6 focus:outline-none"
              @input="e => inputValues(e.target.value, null)"
            >
          </div>
        </div>
        <div class="mx-4 mt-10 w-6 pt-1">
          <div class="h-0 w-full border-b-2 border-gray-800" />
        </div>
        <div class="grow">
          <label for="max_price" class="text-sm text-gray-3">max price</label>
          <div class="flex w-full rounded bg-gray-6 px-4 text-lg font-medium leading-10">
            <span>{{ symbol }}</span>
            <input
              id="max_price"
              :value="Math.round(sliderValue[1] * rate)"
              type="number"
              :min="Math.round(minPrice * rate)"
              :max="Math.round(maxPrice * rate)"
              class="w-full bg-gray-6 focus:outline-none"
              @input="e => inputValues(null, e.target.value)"
            >
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-between">
        <button v-if="filters.min_price || filters.max_price" class="letter-sp15 h-10 text-sm font-medium uppercase leading-10" @click="clearFilter">
          Clear All
        </button>
        <div class="grow text-right">
          <button
            class="letter-sp15 h-10 rounded-md bg-blue px-4 text-sm uppercase leading-10 text-white hover:bg-blue-dark md:px-8"
            :class="{ 'opacity-75': loading, 'cursor-not-allowed': loading }"
            :disabled="loading"
            @click="applyFilter"
          >
            Show
            <span v-if="loading" class="-mt-1 inline-block w-8 align-middle spinner-sm" />
            <span v-else>{{ total }}</span>
            {{ $f.pluralize('result', total, false) }}
          </button>
        </div>
      </div>
    </template>
  </Dropdown>
</template>

<script>
import merge from 'lodash/merge'
import debounce from 'lodash/debounce'
import VueSlider from 'vue-3-slider-component'
import { mapState } from 'pinia'
import Dropdown from '~/components/shared/Dropdown'
import DropdownSelect from '~/components/shared/DropdownSelect'
import decode from '~/utils/decode-ent'
import PricesChart from '~/components/venues/filters/PricesChart'
import { useVenuesStore } from '~/stores/venues'
import { useCurrenciesStore } from '~/stores/currencies'

export default {
  components: { Dropdown, PricesChart, DropdownSelect, VueSlider },

  props: {
    venueType: { type: Object, required: true },
    destination: { type: Object, required: true },
    filters: { type: Object, required: true },
  },

  emits: ['update', 'apply'],

  data: () => ({
    show: false,
    sliderValue: [0, 1000],
    minPrice: 0,
    maxPrice: 1000,
    skipUpdate: false,
  }),

  computed: {
    ...mapState(useVenuesStore, {
      loading: s => s.totals.loading,
      total: s => s.totals.total,
      rangeMinPrice: s => Math.floor(s.ranges.min_price),
      rangeMaxPrice: s => Math.ceil(s.ranges.max_price),
      prices: 'prices',
    }),

    ...mapState(useCurrenciesStore, {
      currencies: s => s.currencies.data,
      currency: 'currency',
      symbol: 'symbol',
      rate: 'rate',
    }),

    selectCurrencies () {
      return this.currencies.map(c => ({
        id: c.code,
        label: decode(`${c.symbol} ${c.code}`),
      }))
    },
  },

  watch: {
    sliderValue () {
      if (this.skipUpdate) { return }
      this.updateFilter()
    },

    currency () {
      this.updateFilter()
    },

    rangeMinPrice (val) {
      this.minPrice = Math.floor(val)
      this.skipUpdate = true
      if (this.sliderValue[0] < this.minPrice) { this.sliderValue = [this.minPrice, this.sliderValue[1]] }
      this.$nextTick(() => (this.skipUpdate = false))
    },

    rangeMaxPrice (val) {
      this.maxPrice = Math.ceil(val)
      this.skipUpdate = true
      if (this.sliderValue[1] > this.maxPrice) { this.sliderValue = [this.sliderValue[0], this.maxPrice] }
      this.$nextTick(() => (this.skipUpdate = false))
    },
  },

  mounted () {
    this.setValues()
  },

  methods: {
    updateCurrency (code) {
      useCurrenciesStore().code = code
    },

    setValues () {
      this.sliderValue = [
        this.filters.min_price || this.minPrice,
        this.filters.max_price || this.maxPrice,
      ]
    },

    getFilter () {
      return {
        min_price: Math.round(this.sliderValue[0]),
        max_price: Math.round(this.sliderValue[1]),
      }
    },

    applyFilter () {
      this.updateValues(this.getFilter())
      this.show = false
    },

    clearFilter () {
      this.updateValues({
        min_price: null,
        max_price: null,
      })
      this.setValues()
      this.show = false
    },

    updateFilter: debounce(function () {
      this.$emit('update', Object.assign({}, this.filters, this.getFilter()))
    }, 500),

    updateValues (values) {
      this.$emit('apply', merge({}, values))
    },

    inputValues (min, max) {
      this.sliderValue = [
        min === null ? this.sliderValue[0] : Math.max(min / this.rate, this.minPrice),
        max === null ? (this.sliderValue[1] / this.rate) : Math.min(max / this.rate, this.maxPrice),
      ]
    },
  },
}
</script>

<style lang="scss">
  .vue-slider {
    height: 2px !important;
  }
  .vue-slider-rail {
    @apply bg-gray-5;
  }
  .vue-slider-process {
    @apply bg-gray-5;
  }
  .vue-slider-dot .vue-slider-dot-handle {
    border-width: 1.5px;
    @apply w-6 h-6 border-gray-mid shadow-none;

    &:after {
      content: ''; width: 9px; height: 9px; top: 7.4px; left: 9px; transform: rotate(-135deg);
      border-right-width: 1.5px; border-top-width: 1.5px;
      @apply absolute border-gray-mid;
    }
  }
  .vue-slider-dot:last-child .vue-slider-dot-handle {
    &:after {
      top: 7.4px; left: 6px; transform: rotate(45deg);
    }
  }
</style>
